import type { ReactNode } from "react";
import type { Table as TableType } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { tv } from "tailwind-variants";

import { EmptyState, icons } from "../common";
import { SortIcon } from "./sort-icon";
import { TableBody } from "./table-body";
import type { RowLinkProps } from "./table-content";
import { TableContent } from "./table-content";
import { TableFooter } from "./table-footer";
import { TableHead } from "./table-head";
import { TableHeader } from "./table-header";
import { TableRow } from "./table-row";
import { TableSkeleton } from "./table-skeleton";
import { TableWrapper } from "./table-wrapper";

const tableVariants = tv({
  variants: {
    /*
      PaginatedTable draws its own card around the table and its footer, so the
      table must be able to render without chrome of its own — two nested
      rounded, bordered boxes are what made the paginated footer seam.
    */
    card: {
      /*
        overflow-hidden is load-bearing: the rows square off the rounded corners
        without it: the scroll container that clips them is nested inside this
        card rather than being the card.
      */
      true: "overflow-hidden rounded-xl border border-line bg-surface shadow-card",
      false: "",
    },
  },
  defaultVariants: {
    card: true,
  },
});

const headerVariants = tv({
  slots: {
    layout: "flex items-start gap-2",
    content: "min-w-0 flex-1",
    iconButton: [
      "inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded",
      "text-content-muted transition-colors duration-150 hover:bg-surface-active hover:text-content",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none",
    ],
    textButton: [
      /*
        The label typography is repeated here rather than inherited from the cell:
        the UA stylesheet sets `text-transform: none` on form controls, so the
        cell's `uppercase` never reached text inside this button and plain-text
        headers rendered lower-case next to upper-case filter labels.
      */
      "text-xs font-semibold tracking-wider uppercase",
      "-mx-1 -my-0.5 flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-left whitespace-nowrap",
      "transition-colors duration-150 hover:text-content",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none",
    ],
  },
});

const { content, iconButton, layout, textButton } = headerVariants();

const SORT_STATE_LABEL = {
  asc: "sorted ascending",
  desc: "sorted descending",
  none: "not sorted",
} as const;

const ARIA_SORT = {
  asc: "ascending",
  desc: "descending",
  none: "none",
} as const;

/*
  Column ids are camelCase ("careplanPendingAction"). A header that renders a
  filter control keeps its human name inside that control, out of reach here, so
  the sort button's accessible name is derived from the id instead.
*/
const humanizeColumnId = (id: string) => {
  return id
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
};

export type TableProps<TData> = {
  className?: string;
  card?: boolean;
  table: TableType<TData>;
  isLoadingData: boolean;
  rowsPerPage: number;
  rowLink?: (row: TData) => RowLinkProps;
  rowLinkLabel?: (row: TData) => string;
  footer?: ReactNode;
};

/*
   Don't use directly, use SinglePageTable or PaginatedTable instead
*/

export const Table = <TData,>({
  card = true,
  className,
  footer,
  isLoadingData,
  rowLink,
  rowLinkLabel,
  rowsPerPage,
  table,
}: TableProps<TData>) => {
  const rows = table.getRowModel().rows;
  const isEmpty = !isLoadingData && rows.length === 0;

  return (
    <div className={tableVariants({ card, className })}>
      {/*
        aria-busy tells assistive tech the rows on screen are placeholders; without
        it the skeleton reads as real, empty data.
      */}
      <TableWrapper aria-busy={isLoadingData}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const sortKey = sorted === false ? "none" : sorted;
                  const definition = header.column.columnDef.header;
                  const rendered = flexRender(definition, header.getContext());

                  if (!header.column.getCanSort()) {
                    return <TableHead key={header.id}>{rendered}</TableHead>;
                  }

                  /*
                    A text header can become one big sort button. A header that
                    renders a filter input cannot: nesting that input inside the
                    button broke typing into it, so the two sit side by side.
                  */
                  if (typeof definition === "string") {
                    return (
                      <TableHead aria-sort={ARIA_SORT[sortKey]} key={header.id}>
                        <button
                          className={textButton()}
                          onClick={header.column.getToggleSortingHandler()}
                          type="button"
                        >
                          {rendered}

                          <SortIcon className="size-3.5 shrink-0 stroke-2" header={header} />

                          <span className="sr-only">({SORT_STATE_LABEL[sortKey]})</span>
                        </button>
                      </TableHead>
                    );
                  }

                  return (
                    <TableHead aria-sort={ARIA_SORT[sortKey]} key={header.id}>
                      <div className={layout()}>
                        <div className={content()}>{rendered}</div>

                        <button
                          aria-label={`Sort by ${humanizeColumnId(header.column.id)}`}
                          className={iconButton()}
                          onClick={header.column.getToggleSortingHandler()}
                          type="button"
                        >
                          <SortIcon className="size-3.5 stroke-2" header={header} />

                          <span className="sr-only">({SORT_STATE_LABEL[sortKey]})</span>
                        </button>
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            );
          })}
        </TableHeader>

        {!isEmpty && (
          <TableBody>
            {isLoadingData ? (
              <TableSkeleton columnsLength={table.getAllColumns().length} numRows={rowsPerPage} />
            ) : (
              <TableContent rowLink={rowLink} rowLinkLabel={rowLinkLabel} rows={rows} />
            )}
          </TableBody>
        )}

        {footer && !isEmpty && !isLoadingData ? <TableFooter>{footer}</TableFooter> : null}
      </TableWrapper>

      {/*
        Outside the horizontal scroll container on purpose: a cell spanning every
        column centres across the table's full scroll width, which puts the
        message off-screen on a wide table until you scroll sideways.
      */}
      {isEmpty ? (
        /*
          A fixed floor, not one scaled to the page size: reserving a row's height
          per page slot gave the 50-row tables 2700px of nothing to fill.
        */
        <div className="flex min-h-80 items-center justify-center">
          <EmptyState
            description="Try adjusting the filters above."
            icon={<icons.MagnifyingGlassIcon className="size-6" />}
            title="No records found"
          />
        </div>
      ) : null}
    </div>
  );
};
Table.displayName = "Table";
